// Base API client with shared configuration and interceptors
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { authService } from '../auth';
import { configService } from '../config';
import { createLogger } from '@/utils/logger';

interface ValidationErrorPayload {
  detail?: unknown;
}

export class BaseApiClient {
  protected api: AxiosInstance;
  protected readonly baseURL: string;
  protected readonly logger: ReturnType<typeof createLogger>;

  constructor(loggerName: string = 'BaseAPI') {
    this.baseURL = configService.apiBaseUrl;
    this.logger = createLogger(loggerName);

    this.api = axios.create({
      baseURL: `${this.baseURL}/api/v1/admin`,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Client': 'MediaHub-Ops-Dashboard',
        'X-Client-Version': configService.appVersion,
      },
    });

    this.setupInterceptors();
  }

  /**
   * Generic HTTP GET method
   */
  async get<T = unknown>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.api.get(endpoint, config);
      return response.data;
    } catch (error) {
      this.logger.error(`GET ${endpoint} failed:`, error);
      throw error;
    }
  }

  /**
   * Generic HTTP POST method
   */
  async post<T = unknown>(endpoint: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.api.post(endpoint, data, config);
      return response.data;
    } catch (error) {
      this.logger.error(`POST ${endpoint} failed:`, error);
      throw error;
    }
  }

  /**
   * Generic HTTP PUT method
   */
  async put<T = unknown>(endpoint: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.api.put(endpoint, data, config);
      return response.data;
    } catch (error) {
      this.logger.error(`PUT ${endpoint} failed:`, error);
      throw error;
    }
  }

  /**
   * Generic HTTP DELETE method
   */
  async delete<T = unknown>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.api.delete(endpoint, config);
      return response.data;
    } catch (error) {
      this.logger.error(`DELETE ${endpoint} failed:`, error);
      throw error;
    }
  }

  /**
   * Generic HTTP PATCH method
   */
  async patch<T = unknown>(endpoint: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.api.patch(endpoint, data, config);
      return response.data;
    } catch (error) {
      this.logger.error(`PATCH ${endpoint} failed:`, error);
      throw error;
    }
  }

  /**
   * Custom query method for flexible API calls
   */
  async customQuery<T = unknown>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    endpoint: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    const url = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

    switch (method) {
      case 'GET':
        return this.api.get<T>(url, config);
      case 'POST':
        return this.api.post<T>(url, data, config);
      case 'PUT':
        return this.api.put<T>(url, data, config);
      case 'PATCH':
        return this.api.patch<T>(url, data, config);
      case 'DELETE':
        return this.api.delete<T>(url, config);
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }
  }

  /**
   * Production-optimized batch operations for large datasets
   */
  async batchRequest<T>(
    requests: Array<{ endpoint: string; method: 'GET' | 'POST'; data?: unknown }>,
    batchSize: number = 10
  ): Promise<T[]> {
    const results: T[] = [];

    for (let i = 0; i < requests.length; i += batchSize) {
      const batch = requests.slice(i, i + batchSize);
      const batchPromises = batch.map(req =>
        this.customQuery<T>(req.method, req.endpoint, req.data)
      );

      try {
        const batchResults = await Promise.allSettled(batchPromises);

        batchResults.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            results.push(result.value.data);
          } else {
            this.logger.error('Batch request failed:', {
              endpoint: batch[index].endpoint,
              error: result.reason
            });
          }
        });

        // Rate limiting consideration for production
        if (configService.isProduction && i + batchSize < requests.length) {
          await new Promise(resolve => setTimeout(resolve, 100)); // 100ms delay
        }
      } catch (error) {
        this.logger.error('Batch processing error:', error);
        throw error;
      }
    }

    return results;
  }

  private setupInterceptors(): void {
    type RetryableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const authHeaders = authService.getAuthHeader();
        Object.assign(config.headers, authHeaders);
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        // Enhanced error handling for production use
        const status = error.response?.status;
        const isNetworkError = error.code === 'NETWORK_ERROR' || !error.response;
        const url = error.config?.url || '';
        const requestConfig = error.config as RetryableConfig | undefined;

        // Log API errors for monitoring (but skip expected 404s for unimplemented endpoints)
        const isExpected404 = status === 404 && (
          url.includes('/vlm/') ||
          url.includes('/reports/') ||
          url.includes('/email/') ||
          url.includes('/stats') // Add stats endpoint 404s as expected
        );

        if (!isExpected404) {
          this.logger.error('Request failed:', {
            url,
            status,
            message: error.message,
            isNetworkError,
            baseURL: this.baseURL
          });
        }

        // Handle authentication errors - sign out on any 401 response
        if (status === 401) {
          const isAuthEndpoint = url.includes('/auth/') ||
            url.includes('/me') ||
            url.includes('/profile');

          if (!isAuthEndpoint && !requestConfig?._retry) {
            if (requestConfig) requestConfig._retry = true;
            const refreshed = await authService.refreshSession();
            if (refreshed && requestConfig) {
              const authHeaders = authService.getAuthHeader();
              Object.assign(requestConfig.headers, authHeaders);
              return this.api.request(requestConfig);
            }
          }

          if (isAuthEndpoint) {
            this.logger.warn('Authentication endpoint failed, signing out');
          } else {
            // Log different messages for context, but always sign out
            if (configService.isProduction) {
              this.logger.warn('Production API authentication failed, signing out:', {
                url,
                message: 'Token expired or invalid - user will be redirected to login'
              });
            } else {
              this.logger.debug('Development API authentication failed, signing out:', {
                url,
                message: 'Backend API authentication failed - user will be redirected to login'
              });
            }
          }

          // Always sign out on 401 to provide seamless user experience
          authService.signOut();
        }

        // Handle rate limiting
        if (status === 429) {
          this.logger.warn('Rate limited, request will be retried');
        }

        // Handle client errors (400-499)
        if (typeof status === 'number' && status >= 400 && status < 500) {
          this.logger.error('Client error:', {
            status,
            url,
            message: error.message,
            responseData: error.response?.data,
            requestData: error.config?.data,
            method: error.config?.method?.toUpperCase(),
            headers: error.config?.headers
          });

          // Additional detailed logging for 400 errors
          if (status === 400) {
            this.logger.debug('400 Bad Request Details:', {
              url: error.config?.url,
              method: error.config?.method?.toUpperCase(),
              requestData: error.config?.data,
              serverResponse: error.response?.data
            });
          }

          // Enhanced logging for 422 Unprocessable Entity errors (validation failures)
          if (status === 422) {
            this.logger.error('422 Unprocessable Entity - Validation Failed:', {
              url: error.config?.url,
              method: error.config?.method?.toUpperCase(),
              tier: error.config?.url?.split('/').pop(),
              serverValidationErrors: error.response?.data,
              environment: configService.getEnvironmentDisplayName()
            });

            // Log validation details for easier debugging
            const serverData = error.response?.data as ValidationErrorPayload | undefined;
            if (Array.isArray(serverData?.detail)) {
              this.logger.error('Validation errors:', serverData.detail);
            }
          }
        }

        // Handle server errors
        if (typeof status === 'number' && status >= 500) {
          this.logger.error('Server error:', { status, message: error.message });
        }

        // Enhanced error object with more context
        const enhancedError = {
          ...error,
          context: {
            url,
            status,
            isNetworkError,
            timestamp: new Date().toISOString(),
          }
        };

        return Promise.reject(enhancedError);
      }
    );
  }

  /**
   * Get API connection status and configuration
   */
  getConnectionInfo(): { baseURL: string; isConfigured: boolean; environment: string; version: string } {
    return {
      baseURL: `${this.baseURL}/api/v1/admin`,
      isConfigured: !!this.baseURL && authService.isAuthenticated(),
      environment: configService.environment,
      version: configService.appVersion
    };
  }

  /**
   * Health check for the API service
   */
  async healthCheck(): Promise<{ healthy: boolean; details?: unknown }> {
    try {
      // Use the actual endpoint: GET /admin/health/api
      // Increased timeout to 15s for development environments with slower startup
      const response = await this.api.get('/health/api', { timeout: 15000 });
      return {
        healthy: response.status === 200,
        details: response.data
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      // Only log error if it's not a timeout (timeout is expected during startup)
      if (axiosError.code !== 'ECONNABORTED') {
        this.logger.error('Health check failed:', error);
      } else {
        this.logger.debug('Health check timeout (expected during startup)');
      }
      return {
        healthy: false,
        details: { error: axiosError.message || 'Unknown error' }
      };
    }
  }
}

